import { useCallback, useEffect, useState } from "react";
import {
  TrendingUp,
  TrendingDown,
  Clock,
  Users,
  Target,
  Trophy,
  Building2,
  Award,
} from "lucide-react";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip as RTooltip,
  CartesianGrid,
  Cell,
} from "recharts";
import AppShell from "@/components/layout/AppShell";
import { analyticsApi } from "@/lib/crmApi";
import { useSettings } from "@/context/SettingsContext";
import { formatFCFA } from "@/lib/crmHelpers";
import ComparisonCards from "@/components/crm/ComparisonCards";

const ACCENTS = {
  navy: { bg: "bg-accent-navy/10 dark:bg-accent-navy/30", text: "text-accent-navy dark:text-blue-300", bar: "bg-accent-navy" },
  gold: { bg: "bg-amber-50 dark:bg-amber-950/40", text: "text-accent-gold", bar: "bg-accent-gold" },
  emerald: { bg: "bg-emerald-50 dark:bg-emerald-950/40", text: "text-emerald-600 dark:text-emerald-400", bar: "bg-emerald-500" },
  red: { bg: "bg-red-50 dark:bg-red-950/40", text: "text-red-600 dark:text-red-400", bar: "bg-red-500" },
};

function KPI({ icon: Icon, label, value, sub, accent = "navy", testId }) {
  const a = ACCENTS[accent];
  return (
    <div
      data-testid={testId}
      className="group relative overflow-hidden rounded-2xl border border-border/60 bg-card p-5 shadow-soft card-hover"
    >
      <div className={`absolute left-0 top-0 h-full w-[3px] ${a.bar}`} aria-hidden />
      <div className="flex items-start justify-between">
        <div className="min-w-0 flex-1">
          <div className="text-label text-muted-foreground">{label}</div>
          <div className="font-heading mt-2 text-3xl font-bold tracking-tight">
            {value}
          </div>
          {sub && (
            <div className="mt-1.5 text-xs text-muted-foreground">{sub}</div>
          )}
        </div>
        <div className={`flex h-10 w-10 flex-none items-center justify-center rounded-xl ${a.bg}`}>
          <Icon className={`h-5 w-5 ${a.text}`} strokeWidth={2} />
        </div>
      </div>
    </div>
  );
}

const CHART_COLORS = [
  "hsl(var(--chart-1))",
  "hsl(var(--chart-2))",
  "hsl(var(--chart-3))",
  "hsl(var(--chart-4))",
  "hsl(var(--chart-5))",
];

export default function Reports() {
  const { activeProjectId, activeProject } = useSettings();
  const [perf, setPerf] = useState(null);
  const [ca, setCa] = useState(null);
  const [byProgramme, setByProgramme] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [p, c, pg] = await Promise.all([
        analyticsApi.performance(activeProjectId),
        analyticsApi.caPotentiel(activeProjectId),
        analyticsApi.conversionByProgramme(activeProjectId),
      ]);
      setPerf(p);
      setCa(c);
      setByProgramme(pg);
    } catch (e) {
      /* ignore */
    } finally {
      setLoading(false);
    }
  }, [activeProjectId]);

  useEffect(() => {
    load();
  }, [load]);

  if (loading || !perf) {
    return (
      <AppShell topbarProps={{ showSearch: false, showAdd: false, onRefresh: load }}>
        <div className="flex min-h-[60vh] items-center justify-center text-muted-foreground">
          Chargement des rapports...
        </div>
      </AppShell>
    );
  }

  const evolution = perf.evolution_pct;
  const isUp = evolution != null && evolution >= 0;

  // Top performers
  const topPerformers = [...(perf.per_commercial || [])]
    .sort((a, b) => b.gagne - a.gagne)
    .slice(0, 5);

  return (
    <AppShell
      topbarProps={{
        showSearch: false,
        showAdd: false,
        onRefresh: load,
      }}
    >
      <div className="mx-auto max-w-[1400px] px-4 py-5 sm:px-6 sm:py-6 lg:px-8 lg:py-8">
        {/* Page header */}
        <div className="mb-6 flex flex-col gap-1">
          <p className="text-label text-muted-foreground">Performance</p>
          <h1 className="font-heading text-2xl font-bold tracking-tight sm:text-3xl">
            Rapports
          </h1>
          <p className="text-sm text-muted-foreground">
            {activeProject?.nom
              ? `Indicateurs consolidés · ${activeProject.nom}`
              : "Vue consolidée tous projets confondus"}
          </p>
        </div>

        {/* v2.4.0 — Comparaison période courante vs précédente, en haut
            de la page Rapports (avant les KPI bruts du mois en cours) */}
        <div className="mb-6">
          <ComparisonCards />
        </div>

        {/* KPIs */}
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <KPI
            icon={Users}
            label="Leads ce mois-ci"
            value={perf.this_month.count}
            sub={
              evolution != null ? (
                <span
                  className={`inline-flex items-center gap-1 font-medium ${
                    isUp ? "text-emerald-600" : "text-red-600"
                  }`}
                >
                  {isUp ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
                  {isUp ? "+" : ""}
                  {evolution}% vs mois dernier
                </span>
              ) : (
                "Premier mois de données"
              )
            }
            accent="navy"
            testId="kpi-this-month"
          />
          <KPI
            icon={Trophy}
            label="Ventes conclues"
            value={perf.this_month.won}
            sub={`Le mois dernier : ${perf.prev_month.won}`}
            accent="emerald"
            testId="kpi-won-month"
          />
          <KPI
            icon={Clock}
            label="Cycle moyen"
            value={perf.avg_cycle_days != null ? `${perf.avg_cycle_days} j` : "—"}
            sub={`${perf.deals_closed} ventes conclues`}
            accent="gold"
            testId="kpi-cycle"
          />
          <KPI
            icon={Target}
            label="CA potentiel"
            value={formatFCFA(ca?.active || 0)}
            sub={`Gagné : ${formatFCFA(ca?.won || 0)}`}
            accent="navy"
            testId="kpi-report-ca"
          />
        </div>

        {/* Top performers + Programs */}
        <div className="mt-6 grid gap-6 lg:grid-cols-3">
          {/* Top performers */}
          <section
            className="rounded-2xl border border-border/60 bg-card p-5 shadow-soft lg:col-span-2"
            data-testid="commercial-table"
          >
            <div className="mb-4 flex items-center justify-between">
              <div>
                <div className="text-label text-muted-foreground">Équipe</div>
                <h2 className="font-heading mt-0.5 flex items-center gap-2 text-lg font-bold">
                  <Award className="h-5 w-5 text-accent-gold" />
                  Performance par commercial
                </h2>
                {/* Make the time scope explicit so the table totals don't
                    get confused with the "Ce mois" KPI block above. The
                    KPI is leads CREATED this month, the table is the
                    cumulative breakdown across the project history. */}
                <p className="mt-0.5 text-[11px] text-muted-foreground">
                  Cumul depuis le début du projet · totaux incluent les leads gagnés et perdus
                </p>
              </div>
              <span className="text-xs text-muted-foreground">
                {perf.per_commercial.length} commercial{perf.per_commercial.length > 1 ? "(aux)" : ""}
              </span>
            </div>

            {/* Chart on top */}
            {topPerformers.length > 0 && (
              <div className="mb-4 h-44 w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={topPerformers} layout="vertical" margin={{ left: 0, right: 16 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" horizontal={false} />
                    <XAxis type="number" tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }} stroke="hsl(var(--border))" />
                    <YAxis dataKey="commercial" type="category" tick={{ fontSize: 11, fill: "hsl(var(--foreground))" }} stroke="hsl(var(--border))" width={100} />
                    <RTooltip
                      contentStyle={{
                        background: "hsl(var(--popover))",
                        border: "1px solid hsl(var(--border))",
                        borderRadius: 8,
                        fontSize: 12,
                      }}
                    />
                    <Bar dataKey="gagne" name="Gagnés" radius={[0, 4, 4, 0]}>
                      {topPerformers.map((_, i) => (
                        <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}

            {/* Mobile: cards / Desktop: table */}
            <div className="hidden md:block">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border/60">
                      <th className="pb-2 text-left text-label font-semibold text-muted-foreground">Commercial</th>
                      <th className="pb-2 text-right text-label font-semibold text-muted-foreground">Total</th>
                      <th className="pb-2 text-right text-label font-semibold text-muted-foreground">Actifs</th>
                      <th className="pb-2 text-right text-label font-semibold text-muted-foreground">HOT</th>
                      <th className="pb-2 text-right text-label font-semibold text-muted-foreground">Gagnés</th>
                      <th className="pb-2 text-right text-label font-semibold text-muted-foreground">Perdus</th>
                      <th className="pb-2 text-right text-label font-semibold text-muted-foreground">Conv.</th>
                    </tr>
                  </thead>
                  <tbody>
                    {perf.per_commercial.map((c) => (
                      <tr
                        key={c.commercial}
                        className="border-b border-border/40 last:border-0 transition-colors hover:bg-muted/30"
                        data-testid={`commercial-row-${c.commercial.replace(/\s+/g, "-")}`}
                      >
                        <td className="py-2.5 font-medium">{c.commercial}</td>
                        <td className="py-2.5 text-right">{c.total}</td>
                        <td className="py-2.5 text-right">{c.actif}</td>
                        <td className="py-2.5 text-right">
                          {c.hot > 0 && (
                            <span className="inline-flex items-center gap-0.5 rounded-full bg-red-50 px-1.5 py-0.5 text-xs font-semibold text-red-700 dark:bg-red-950/40 dark:text-red-300">
                              {c.hot}
                            </span>
                          )}
                        </td>
                        <td className="py-2.5 text-right font-semibold text-emerald-600 dark:text-emerald-400">
                          {c.gagne}
                        </td>
                        <td className="py-2.5 text-right text-muted-foreground">{c.perdu}</td>
                        <td className="py-2.5 text-right font-semibold">
                          {c.taux_conversion}%
                        </td>
                      </tr>
                    ))}
                    {perf.per_commercial.length === 0 && (
                      <tr>
                        <td colSpan={7} className="py-6 text-center text-muted-foreground">
                          Aucun commercial assigné pour le moment.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Mobile cards */}
            <div className="space-y-2 md:hidden">
              {perf.per_commercial.map((c) => (
                <div
                  key={c.commercial}
                  className="rounded-lg border border-border/60 bg-muted/20 p-3"
                  data-testid={`commercial-card-${c.commercial.replace(/\s+/g, "-")}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="font-heading font-semibold">{c.commercial}</div>
                    <div className="font-heading text-lg font-bold">{c.taux_conversion}%</div>
                  </div>
                  <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
                    <span>Total : <strong className="text-foreground">{c.total}</strong></span>
                    <span>Actifs : <strong className="text-foreground">{c.actif}</strong></span>
                    {c.hot > 0 && <span className="text-red-600">HOT : <strong>{c.hot}</strong></span>}
                    <span className="text-emerald-600">Gagnés : <strong>{c.gagne}</strong></span>
                    <span>Perdus : <strong className="text-foreground">{c.perdu}</strong></span>
                  </div>
                </div>
              ))}
              {perf.per_commercial.length === 0 && (
                <p className="rounded-lg border border-dashed border-border/60 p-6 text-center text-sm text-muted-foreground">
                  Aucun commercial assigné.
                </p>
              )}
            </div>
          </section>

          {/* Conversion summary */}
          <section
            className="rounded-2xl border border-border/60 bg-card p-5 shadow-soft"
            data-testid="conversion-summary"
          >
            <div className="mb-4">
              <div className="text-label text-muted-foreground">Synthèse</div>
              <h2 className="font-heading mt-0.5 text-lg font-bold">
                Vue d'ensemble
              </h2>
            </div>

            <div className="space-y-4">
              <div className="rounded-xl bg-gradient-to-br from-accent-navy to-accent-blue p-4 text-white">
                <div className="text-[11px] uppercase tracking-wide text-white/70">Taux de closing global</div>
                <div className="font-heading mt-1 text-3xl font-bold">
                  {perf.deals_closed > 0
                    ? Math.round((perf.this_month.won / Math.max(perf.this_month.count, 1)) * 100)
                    : 0}
                  %
                </div>
                <div className="mt-1 text-xs text-white/70">ce mois-ci</div>
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Ce mois</span>
                  <span className="font-semibold">{perf.this_month.count} leads</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Mois dernier</span>
                  <span className="font-semibold">{perf.prev_month.count} leads</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Ventes conclues</span>
                  <span className="font-semibold text-emerald-600">{perf.this_month.won}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Cycle moyen</span>
                  <span className="font-semibold">
                    {perf.avg_cycle_days != null ? `${perf.avg_cycle_days} j` : "—"}
                  </span>
                </div>
              </div>
            </div>
          </section>
        </div>

        {/* Per programme */}
        <section
          className="mt-6 rounded-2xl border border-border/60 bg-card p-5 shadow-soft"
          data-testid="programme-performance"
        >
          <div className="mb-4">
            <div className="text-label text-muted-foreground">Programmes</div>
            <h2 className="font-heading mt-0.5 flex items-center gap-2 text-lg font-bold">
              <Building2 className="h-5 w-5 text-accent-gold" />
              Performance par programme immobilier
            </h2>
          </div>

          {byProgramme.length === 0 ? (
            <p className="rounded-lg border border-dashed border-border/60 p-6 text-center text-sm text-muted-foreground">
              Pas encore de données par programme.
            </p>
          ) : (
            <div className="space-y-3">
              {byProgramme.map((p) => (
                <div
                  key={p.programme}
                  className="grid grid-cols-1 items-center gap-3 rounded-xl border border-border/40 bg-muted/20 p-4 sm:grid-cols-[1fr_auto_auto_auto] card-hover"
                >
                  <div>
                    <div className="font-heading font-semibold">{p.programme}</div>
                    <div className="text-xs text-muted-foreground">
                      {p.total} lead(s) · {p.actif} actif(s)
                    </div>
                  </div>
                  <div className="text-right text-sm">
                    <div className="font-semibold text-emerald-600 dark:text-emerald-400">
                      {p.gagne} gagné(s)
                    </div>
                  </div>
                  <div className="text-right text-sm text-muted-foreground">
                    {p.perdu} perdu(s)
                  </div>
                  <div className="text-right">
                    <div className="font-heading text-xl font-bold">
                      {p.taux_conversion}%
                    </div>
                    <div className="text-[11px] text-muted-foreground">conversion</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      </div>
    </AppShell>
  );
}
